import { MercatorLngLat } from '@xyo-network/sdk-xyo-js'

import { XyoWorldBase } from '../../../Base'
import { BaseJsonObject } from '../../../models'
import { Quadkey } from '../../../Quadkey'
import { AuctionJsonObject, XyoWorldAuction } from '../Auction'
import { MinterContracts } from '../MinterContracts'

export interface AuctionFactoryJsonObject extends BaseJsonObject {
  auctions: AuctionJsonObject[]
}

export class XyoWorldAuctionFactory extends XyoWorldBase<AuctionFactoryJsonObject> {
  public minters: MinterContracts

  private map = new Map<string, XyoWorldAuction>()

  constructor(minters: MinterContracts) {
    super()
    this.minters = minters
  }

  public clear() {
    this.map.clear()
    return this
  }

  public async fetch(quadkey: Quadkey) {
    const result = this.get(quadkey)
    await result.refresh()
    return result
  }

  public get(quadkey: Quadkey, jsonObject?: AuctionJsonObject): XyoWorldAuction {
    let result = this.map.get(quadkey.toHex())
    if (!result) {
      result = new XyoWorldAuction(quadkey, this.minters, jsonObject)
      this.map.set(quadkey.toHex(), result)
    } else {
      result.loadJsonObject(jsonObject)
    }
    return result
  }

  public getAvailableByLngLat(point: MercatorLngLat) {
    let zoom = 1
    let quadkey = Quadkey.fromLngLat(point, zoom)
    if (!quadkey) {
      throw 'Base Quadkey!'
    }
    let auction = this.get(quadkey)
    while (auction.static.minted) {
      zoom += 1
      quadkey = Quadkey.fromLngLat(point, zoom)
      if (!quadkey) {
        throw 'Base Quadkey!'
      }
      auction = this.get(quadkey)
    }
    return auction
  }

  loadJsonObject(jsonObject?: AuctionFactoryJsonObject) {
    if (jsonObject) {
      for (const auctionJsonObject of jsonObject.auctions) {
        const quadkey = Quadkey.fromBase4String(auctionJsonObject.quadkey)
        if (quadkey) {
          this.get(quadkey, auctionJsonObject)
        }
      }
    }
    return this
  }

  toJsonObject(): AuctionFactoryJsonObject {
    const auctions: AuctionJsonObject[] = []
    this.map.forEach((value) => {
      auctions.push(value.toJsonObject())
    })
    return { auctions }
  }

  public async update() {
    const promises: Promise<XyoWorldAuction>[] = []
    for (const pair of this.map) {
      promises.push(pair[1].update())
    }
    await Promise.all(promises)
    return this
  }
}
